import path from 'node:path';
import type { Plugin } from 'vite';
import { init, parse } from 'es-module-lexer';
import { outdent } from 'outdent';
import { minimatch } from 'minimatch';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { WEB_METHOD_PATTERN } from '../../dev-center/components/backend/components.js';
import { normalizeGlobbyPattern } from '../../dev-center/components/path.js';

const postfixRE = /[?#].*$/;

function cleanUrl(url: string): string {
  return url.replace(postfixRE, '');
}

const normalizedWebMethodPattern = normalizeGlobbyPattern(WEB_METHOD_PATTERN);

export function webMethodsPlugin({
  projectFolder,
}: {
  projectFolder: string;
}): Plugin {
  return {
    name: 'web-methods',
    async transform(code, id) {
      const url = cleanUrl(id);

      if (!id.endsWith('.web.ts')) {
        return;
      }

      const relativeId = normalizeGlobbyPattern(
        path.relative(projectFolder, id)
      );

      if (!minimatch(relativeId, normalizedWebMethodPattern)) {
        throw new CliError({
          cause: null,
          code: CliErrorCode.WebMethodOutsideOfProjectDirectory({
            relativeId,
          }),
        });
      }

      await init;

      const [, exports] = parse(code);

      // relative paths should be posix style, as they are used in JS for import statements later
      const pathToFile = normalizeGlobbyPattern(
        path.relative(projectFolder, url)
      );

      const mappedExports = exports.map(
        ({ ln: method }) =>
          outdent`
            export const ${method} = async (...args) => {
              return await callWebMethod({
                args,
                filename: '${pathToFile}',
                method: '${method}',
                baseURL: __webMethodsBaseUrl,
              });
            }
          `
      );

      return outdent`
        import { callWebMethod } from '@wix/cli-app/internal/platform-sdk/web-methods';

        const __webMethodsBaseUrl = import.meta.env.BASE_API_URL.replace(
          '/functions',
          ''
        );

        ${mappedExports.join('\n')}
      `;
    },
  };
}
